/**
 * Types + fetchery dla pluginu `c_resource_reservation` rozszerzajacego shell
 * `c_user_accounts` (rezerwacje usera - lista nadchodzacych + anulowanie).
 *
 * Backend (IRESTAPI, routing po NAZWIE METODY - `IAPI_` strip):
 *   - GET  /IRESTAPI/v1/C_RESOURCE_RESERVATION_CONTROLLER/userReservations               (caly zbior + truncated)
 *   - POST /IRESTAPI/v1/C_RESOURCE_RESERVATION_CONTROLLER/userCancelReservation/{id}     (soft-cancel)
 *   - GET  /IRESTAPI/v1/C_RESOURCE_RESERVATION_CONTROLLER/userResources                  (lista zasobow do rezerwacji)
 *   - GET  /IRESTAPI/v1/C_RESOURCE_RESERVATION_CONTROLLER/resourceDayAvailability/{id}   (sloty na dzien, ?date=)
 *   - POST /IRESTAPI/v1/C_RESOURCE_RESERVATION_CONTROLLER/saveReservation/{id}           (zapis terminu)
 *
 * Tworzenie nowej rezerwacji = slot picker (BEZ Syncfusion): backend generuje sloty
 * z okien dostepnosci zasobu, frontend renderuje wybor, backend waliduje zapis.
 *
 * Frontend route handlery (proxy + Bearer) w `app/api/iiapi/c-resource-reservation/`.
 */

export type UserReservation = {
  id: number;
  add_date: string;
  /** Poczatek rezerwacji (Y-m-d H:i:s). */
  start_date: string;
  /** Koniec rezerwacji (Y-m-d H:i:s). */
  end_date: string;
  /** Nazwa zasobu. */
  resource: string;
  /** Czy mozna anulowac (>3h przed startem). */
  cancellable: boolean;
};

/**
 * Pobiera liste nadchodzacych rezerwacji usera z route handler proxy.
 *
 * @throws Error gdy fetch fail / response !ok.
 */
export async function fetchUserReservations(): Promise<{
  reservations: UserReservation[];
  truncated: boolean;
}> {
  const res = await fetch("/api/iiapi/c-resource-reservation/reservations", { cache: "no-store" });
  if (!res.ok) throw new Error(`Fetch reservations failed: HTTP ${res.status}`);
  const body = (await res.json()) as
    | { ok: true; data: { reservations: UserReservation[]; truncated: boolean } }
    | { ok: false; message?: string };
  if (!("ok" in body) || body.ok !== true) {
    throw new Error("message" in body ? (body.message ?? "Fetch failed") : "Invalid response");
  }
  return {
    reservations: body.data?.reservations ?? [],
    truncated: body.data?.truncated ?? false,
  };
}

/**
 * Anuluje rezerwacje usera (soft-cancel). Zwraca true gdy anulowano.
 *
 * @throws Error gdy fetch fail / response !ok.
 */
export async function cancelReservation(id: number): Promise<boolean> {
  const res = await fetch(`/api/iiapi/c-resource-reservation/reservation/${id}/cancel`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({}),
  });
  const body = (await res.json()) as
    | { ok: true; data: { cancelled: boolean } }
    | { ok: false; message?: string };
  if (!res.ok || !("ok" in body) || body.ok !== true) {
    throw new Error("message" in body ? (body.message ?? "Cancel failed") : "Invalid response");
  }
  return body.data?.cancelled ?? false;
}

/* ─── Tworzenie nowej rezerwacji (slot picker) ─── */

export type BookableResource = {
  id: number;
  name: string;
  description: string;
  /** Maks. liczba osob (0 = bez limitu). */
  max_people: number;
};

export type ReservationSlot = {
  /** Poczatek slotu (Y-m-d H:i:s). */
  start: string;
  /** Koniec slotu (Y-m-d H:i:s). */
  end: string;
  /** Etykieta "HH:MM - HH:MM". */
  label: string;
  /** Czy slot jest juz zajety (nakłada sie z rezerwacja). */
  taken: boolean;
};

export type DayAvailability = {
  resource_id: number;
  date: string;
  /** Czy zasob jest zawsze dostepny (always_availability). */
  always: boolean;
  slots: ReservationSlot[];
};

/**
 * Pobiera liste zasobow do rezerwacji.
 *
 * @throws Error gdy fetch fail / response !ok.
 */
export async function fetchBookableResources(): Promise<{
  resources: BookableResource[];
  truncated: boolean;
}> {
  const res = await fetch("/api/iiapi/c-resource-reservation/resources", { cache: "no-store" });
  if (!res.ok) throw new Error(`Fetch resources failed: HTTP ${res.status}`);
  const body = (await res.json()) as
    | { ok: true; data: { resources: BookableResource[]; truncated: boolean } }
    | { ok: false; message?: string };
  if (!("ok" in body) || body.ok !== true) {
    throw new Error("message" in body ? (body.message ?? "Fetch failed") : "Invalid response");
  }
  return {
    resources: body.data?.resources ?? [],
    truncated: body.data?.truncated ?? false,
  };
}

/**
 * Pobiera dostepne sloty rezerwacji zasobu w danym dniu (Y-m-d).
 *
 * @throws Error gdy fetch fail / response !ok.
 */
export async function fetchDayAvailability(
  resourceId: number,
  date: string,
): Promise<DayAvailability> {
  const res = await fetch(
    `/api/iiapi/c-resource-reservation/resource/${resourceId}/availability?date=${encodeURIComponent(date)}`,
    { cache: "no-store" },
  );
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { message?: string } | null;
    throw new Error(body?.message ?? `Fetch availability failed: HTTP ${res.status}`);
  }
  const body = (await res.json()) as
    | { ok: true; data: DayAvailability }
    | { ok: false; message?: string };
  if (!("ok" in body) || body.ok !== true) {
    throw new Error("message" in body ? (body.message ?? "Fetch failed") : "Invalid response");
  }
  return body.data;
}

/**
 * Zapisuje rezerwacje wybranego slotu. Zwraca true gdy zarezerwowano.
 * Backend waliduje autorytatywnie (dostepnosc + brak kolizji).
 *
 * @throws Error gdy fetch fail / response !ok (z komunikatem backendu).
 */
export async function saveReservation(
  resourceId: number,
  startDate: string,
  endDate: string,
): Promise<boolean> {
  const res = await fetch(`/api/iiapi/c-resource-reservation/resource/${resourceId}/reserve`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ start_date: startDate, end_date: endDate }),
  });
  const body = (await res.json()) as
    | { ok: true; data: { reserved: boolean; id: number } }
    | { ok: false; message?: string };
  if (!res.ok || !("ok" in body) || body.ok !== true) {
    throw new Error("message" in body ? (body.message ?? "Reserve failed") : "Invalid response");
  }
  return body.data?.reserved ?? false;
}
